import { ref } from 'vue';
import { branchApi, useBranchLogo } from '@/entities/Branch';

function useImageUpload() {
  const { preloadUserLogos } = useBranchLogo();
  const hasImages = ref(false);
  const allUploadsComplete = ref(false);
  const uploadStatus = ref<Map<string, 'pending' | 'uploading' | 'completed' | 'error'>>(new Map());
  const loadingRequest = ref<'idle' | 'loading' | 'success' | 'error'>('idle');
  const uploadedImages = ref<File[]>([]);
  const imageProgress = ref<Map<string, number>>(new Map());
  const images = ref<File[]>([]);
  const uploadProgress = ref<Map<string, number>>(new Map());
  const formData = new FormData();
  const uploadedImageIds = ref<number[]>([]);
  const imageToImageIdMap = ref<Map<string, number>>(new Map());

  const handleImageUpload = (newImages: File[]) => {
    uploadedImages.value = newImages;
    hasImages.value = newImages.length > 0;
    allUploadsComplete.value = false;
    uploadedImageIds.value = [];

    images.value = [...newImages];

    formData.delete('file');
    if (newImages.length > 0) {
      formData.append('file', newImages[0]);
    }
  };

  const handleImageProgress = (imageId: string, progress: number) => {
    imageProgress.value.set(imageId, progress);
    uploadProgress.value.set(imageId, progress);
  };

  const handleImageComplete = (imageIds?: number[]) => {
    let complete = true;
    imageProgress.value.forEach((progress) => {
      if (progress < 99) complete = false;
    });
    allUploadsComplete.value = complete;

    if (imageIds && imageIds.length > 0) {
      uploadedImageIds.value = imageIds;

      images.value.forEach((image, index) => {
        if (imageIds[index]) {
          const imageKey = `${image.name}-${image.size}-${image.lastModified}`;
          imageToImageIdMap.value.set(imageKey, imageIds[index]);
        }
      });
    }
  };

  const handleImagesSelected = (
    newImages: FileList | null,
    filterImages: (images: FileList) => File[],
    showError: (message: string) => void,
    onImagesSelected?: (images: File[]) => void,
    onUploadComplete?: (imageId: string, imageIds?: number[]) => void,
    onProgressUpdate?: (imageId: string, progress: number) => void
  ) => {
    if (!newImages || newImages.length === 0) return;

    const filteredImages = filterImages(newImages);

    if (filteredImages.length === 0) {
      showError('Somente arquivos PNG e SVG são permitidos.');
      return;
    }

    const image = filteredImages[0];

    const imageId = `${Date.now()}-${image.name}`;
    uploadProgress.value.set(imageId, 0);
    uploadStatus.value.set(imageId, 'pending');

    images.value = [image];

    formData.delete('file');
    formData.append('file', image);

    if (onImagesSelected) {
      onImagesSelected(images.value);
    }

    uploadStatus.value.set(imageId, 'completed');
    handleImageProgress(imageId, 100);

    if (onProgressUpdate) {
      onProgressUpdate(imageId, 100);
    }

    if (onUploadComplete) {
      onUploadComplete(imageId);
    }
  };

  const createBranch = async (branchData?: {
    name: string;
    colorPrimary: string;
    colorSecondary: string;
    colorText: string;
    colorPrimaryDark?: string;
    colorSecondaryDark?: string;
    colorTextDark?: string;
  }) => {
    try {
      loadingRequest.value = 'loading';

      if (images.value.length === 0) {
        console.error('createBranch: No image selected');
        throw new Error('No image selected');
      }

      if (branchData) {
        Object.entries(branchData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.delete(key);
            formData.append(key, value);
          }
        });
      }

      const image = images.value[0];

      const response = await branchApi.createBranch(formData);

      const createdBranch = response.data;
      const branchId = createdBranch.id;

      if (branchId && image) {
        uploadedImageIds.value = [branchId];

        const imageKey = `${image.name}-${image.size}-${image.lastModified}`;
        imageToImageIdMap.value.set(imageKey, branchId);
      }

      loadingRequest.value = 'success';
      await preloadUserLogos();

      return response;
    } catch (error) {
      console.error('createBranch: Error occurred', error);
      if (Image) {
        const image = images.value[0];
        const imageId = image ? `${Date.now()}-${image.name}` : `${Date.now()}`;
        uploadStatus.value.set(imageId, 'error');
      }
      loadingRequest.value = 'error';
      throw error;
    }
  };

  const getImageStatus = (image: File) => {
    const imageId = Array.from(uploadStatus.value.keys()).find((id) => id.includes(image.name));
    return imageId ? uploadStatus.value.get(imageId) || 'pending' : 'pending';
  };

  const clearState = () => {
    images.value = [];
    uploadedImages.value = [];
    hasImages.value = false;
    allUploadsComplete.value = false;
    imageProgress.value = new Map();
    uploadProgress.value = new Map();
    uploadStatus.value = new Map();
    uploadedImageIds.value = [];
    imageToImageIdMap.value = new Map();
    formData.values();
    formData.delete('file');
    loadingRequest.value = 'idle';
    images.value = [];
  };

  const removeImage = (imageToRemove: File) => {
    const index = images.value.findIndex(
      (img) =>
        img.name === imageToRemove.name &&
        img.size === imageToRemove.size &&
        img.lastModified === imageToRemove.lastModified
    );

    if (index >= 0) {
      clearState();

      return true;
    }
    return false;
  };

  const updateBranch = async (
    branchId: string,
    branchData?: Partial<{
      name: string;
      colorPrimary: string;
      colorSecondary: string;
      colorText: string;
    }>
  ) => {
    try {
      loadingRequest.value = 'loading';

      const hasNewImage =
        images.value.length > 0 &&
        (!(images.value[0] instanceof File) ||
          (images.value[0] instanceof File && images.value[0].size > 0));

      const updateFormData = new FormData();

      if (hasNewImage && images.value[0] instanceof File) {
        updateFormData.append('file', images.value[0]);
      }

      if (branchData) {
        Object.entries(branchData).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            updateFormData.append(key, String(value));
          }
        });
      }

      const image = hasNewImage ? images.value[0] : null;

      const response = await branchApi.updateBranch(branchId, updateFormData);

      const updatedBranch = response.data;
      const updatedBranchId = updatedBranch.id;

      if (updatedBranchId && image) {
        uploadedImageIds.value = [updatedBranchId];

        const imageKey = `${image.name}-${image.size}-${image.lastModified}`;
        imageToImageIdMap.value.set(imageKey, updatedBranchId);
      }

      loadingRequest.value = 'success';
      await preloadUserLogos();

      return response;
    } catch (error) {
      console.error('Update error:', error);
      if (images.value) {
        const image = images.value[0];
        const imageId = image ? `${Date.now()}-${image.name}` : `${Date.now()}`;
        uploadStatus.value.set(imageId, 'error');
      }
      loadingRequest.value = 'error';
      throw error;
    }
  };

  return {
    images,
    uploadedImages,
    hasImages,
    allUploadsComplete,
    imageProgress,
    uploadedImageIds,
    imageToImageIdMap,
    handleImageUpload,
    handleImageProgress,
    handleImageComplete,
    handleImagesSelected,
    getImageStatus,
    removeImage,
    uploadProgress,
    createBranch,
    updateBranch,
    loadingRequest,
    uploadStatus,
    clearState,
  };
}

export default useImageUpload;
